/**
 * GAオペレータ - 交叉・突然変異
 *
 * 遺伝的アルゴリズムの進化オペレータを提供。
 */

import {
  type AIGenome,
  PARAM_RANGES,
  cloneGenome,
  clamp,
  normalizeGenome,
} from './genome';

// =============================================================================
// 交叉オペレータ
// =============================================================================

/**
 * ブレンド交叉（BLX-α）
 *
 * 両親の値を線形補間し、子を生成。
 * alphaパラメータで探索範囲を調整可能。
 *
 * @param parent1 - 親1
 * @param parent2 - 親2
 * @param alpha - 探索範囲拡張率（0.0-0.5、デフォルト0.3）
 * @returns 子ゲノム
 */
export function blendCrossover(
  parent1: AIGenome,
  parent2: AIGenome,
  alpha: number = 0.3
): AIGenome {
  const child = cloneGenome(parent1);
  const childRecord = child as unknown as Record<string, Record<string, number>>;
  const p1Record = parent1 as unknown as Record<string, Record<string, number>>;
  const p2Record = parent2 as unknown as Record<string, Record<string, number>>;

  for (const [category, params] of Object.entries(PARAM_RANGES)) {
    if (typeof params !== 'object' || params === null) continue;

    for (const [param, range] of Object.entries(params)) {
      if (typeof range !== 'object' || range === null || !('min' in range) || !('max' in range)) continue;
      if (!p1Record[category] || !p2Record[category]) continue;
      if (p1Record[category][param] === undefined || p2Record[category][param] === undefined) continue;

      const v1 = p1Record[category][param];
      const v2 = p2Record[category][param];
      const min = Math.min(v1, v2);
      const max = Math.max(v1, v2);
      const d = max - min;

      // BLX-αの範囲で子の値を生成
      const lower = min - alpha * d;
      const upper = max + alpha * d;
      const value = lower + Math.random() * (upper - lower);

      if (!childRecord[category]) {
        childRecord[category] = {};
      }
      childRecord[category][param] = clamp(value, range.min, range.max);
    }
  }

  return child;
}

/**
 * 一様交叉
 *
 * 各パラメータを50%の確率で親1か親2から継承。
 *
 * @param parent1 - 親1
 * @param parent2 - 親2
 * @returns 子ゲノム
 */
export function uniformCrossover(
  parent1: AIGenome,
  parent2: AIGenome
): AIGenome {
  const child = cloneGenome(parent1);
  const childRecord = child as unknown as Record<string, Record<string, number>>;
  const p2Record = parent2 as unknown as Record<string, Record<string, number>>;

  for (const [category, params] of Object.entries(PARAM_RANGES)) {
    if (typeof params !== 'object' || params === null) continue;

    for (const [param] of Object.entries(params)) {
      if (!childRecord[category] || !p2Record[category]) continue;
      if (p2Record[category][param] === undefined) continue;

      // 50%の確率で親2の値を採用
      if (Math.random() < 0.5) {
        childRecord[category][param] = p2Record[category][param];
      }
    }
  }

  return child;
}

/**
 * 算術交叉
 *
 * 両親の値の加重平均で子を生成。
 *
 * @param parent1 - 親1
 * @param parent2 - 親2
 * @param weight - 親1の重み（0.0-1.0、デフォルト0.5）
 * @returns 子ゲノム
 */
export function arithmeticCrossover(
  parent1: AIGenome,
  parent2: AIGenome,
  weight: number = 0.5
): AIGenome {
  const child = cloneGenome(parent1);
  const childRecord = child as unknown as Record<string, Record<string, number>>;
  const p1Record = parent1 as unknown as Record<string, Record<string, number>>;
  const p2Record = parent2 as unknown as Record<string, Record<string, number>>;

  for (const [category, params] of Object.entries(PARAM_RANGES)) {
    if (typeof params !== 'object' || params === null) continue;

    for (const [param] of Object.entries(params)) {
      if (!p1Record[category] || !p2Record[category]) continue;
      if (p1Record[category][param] === undefined || p2Record[category][param] === undefined) continue;

      const v1 = p1Record[category][param];
      const v2 = p2Record[category][param];
      if (!childRecord[category]) {
        childRecord[category] = {};
      }
      childRecord[category][param] = weight * v1 + (1 - weight) * v2;
    }
  }

  return normalizeGenome(child);
}

// =============================================================================
// 突然変異オペレータ
// =============================================================================

/**
 * ガウス突然変異
 *
 * 各パラメータに正規分布のノイズを加える。
 *
 * @param genome - 変異対象ゲノム
 * @param rate - 各パラメータの変異確率（0.0-1.0）
 * @param strength - 変異強度（標準偏差の係数、0.0-1.0）
 * @returns 変異後のゲノム
 */
export function gaussianMutation(
  genome: AIGenome,
  rate: number = 0.1,
  strength: number = 0.2
): AIGenome {
  const mutated = cloneGenome(genome);
  const mutatedRecord = mutated as unknown as Record<string, Record<string, number>>;

  for (const [category, params] of Object.entries(PARAM_RANGES)) {
    if (typeof params !== 'object' || params === null) continue;

    for (const [param, range] of Object.entries(params)) {
      if (typeof range !== 'object' || range === null || !('min' in range) || !('max' in range)) continue;
      if (!mutatedRecord[category] || mutatedRecord[category][param] === undefined) continue;

      if (Math.random() < rate) {
        // Box-Muller変換で正規分布を生成
        const u1 = Math.random();
        const u2 = Math.random();
        const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);

        // 変異量を計算
        const rangeSize = range.max - range.min;
        const delta = z * strength * rangeSize;

        mutatedRecord[category][param] = clamp(
          mutatedRecord[category][param] + delta,
          range.min,
          range.max
        );
      }
    }
  }

  return mutated;
}

/**
 * 一様突然変異
 *
 * 変異するパラメータを範囲内でランダムに置き換える。
 *
 * @param genome - 変異対象ゲノム
 * @param rate - 各パラメータの変異確率（0.0-1.0）
 * @returns 変異後のゲノム
 */
export function uniformMutation(
  genome: AIGenome,
  rate: number = 0.1
): AIGenome {
  const mutated = cloneGenome(genome);
  const mutatedRecord = mutated as unknown as Record<string, Record<string, number>>;

  for (const [category, params] of Object.entries(PARAM_RANGES)) {
    if (typeof params !== 'object' || params === null) continue;

    for (const [param, range] of Object.entries(params)) {
      if (typeof range !== 'object' || range === null || !('min' in range) || !('max' in range)) continue;
      if (!mutatedRecord[category] || mutatedRecord[category][param] === undefined) continue;

      if (Math.random() < rate) {
        // 範囲内でランダムに値を設定
        mutatedRecord[category][param] = range.min + Math.random() * (range.max - range.min);
      }
    }
  }

  return mutated;
}

/**
 * 境界突然変異
 *
 * 変異するパラメータを範囲の最小値か最大値に設定。
 *
 * @param genome - 変異対象ゲノム
 * @param rate - 各パラメータの変異確率（0.0-1.0）
 * @returns 変異後のゲノム
 */
export function boundaryMutation(
  genome: AIGenome,
  rate: number = 0.05
): AIGenome {
  const mutated = cloneGenome(genome);
  const mutatedRecord = mutated as unknown as Record<string, Record<string, number>>;

  for (const [category, params] of Object.entries(PARAM_RANGES)) {
    if (typeof params !== 'object' || params === null) continue;

    for (const [param, range] of Object.entries(params)) {
      if (typeof range !== 'object' || range === null || !('min' in range) || !('max' in range)) continue;
      if (!mutatedRecord[category] || mutatedRecord[category][param] === undefined) continue;

      if (Math.random() < rate) {
        // 50%で最小値、50%で最大値
        mutatedRecord[category][param] = Math.random() < 0.5 ? range.min : range.max;
      }
    }
  }

  return mutated;
}

// =============================================================================
// 選択オペレータ
// =============================================================================

/**
 * 個体と適応度のペア
 */
export interface Individual {
  genome: AIGenome;
  fitness: number;
}

/**
 * トーナメント選択
 *
 * ランダムにk個体を選び、最も適応度の高い個体を返す。
 *
 * @param population - 個体群
 * @param tournamentSize - トーナメントサイズ
 * @returns 選択された個体
 */
export function tournamentSelection(
  population: Individual[],
  tournamentSize: number = 5
): Individual {
  let best: Individual | null = null;

  for (let i = 0; i < tournamentSize; i++) {
    const idx = Math.floor(Math.random() * population.length);
    const candidate = population[idx];

    if (!best || candidate.fitness > best.fitness) {
      best = candidate;
    }
  }

  return best!;
}

/**
 * ルーレット選択（適応度比例選択）
 *
 * 適応度に比例した確率で個体を選択。
 *
 * @param population - 個体群
 * @returns 選択された個体
 */
export function rouletteSelection(
  population: Individual[]
): Individual {
  // 最小適応度を0に正規化
  const minFitness = Math.min(...population.map(i => i.fitness));
  const adjusted = population.map(i => ({
    ...i,
    adjustedFitness: i.fitness - minFitness + 1, // +1で全て正に
  }));

  const totalFitness = adjusted.reduce((sum, i) => sum + i.adjustedFitness, 0);
  let rand = Math.random() * totalFitness;

  for (const individual of adjusted) {
    rand -= individual.adjustedFitness;
    if (rand <= 0) {
      return individual;
    }
  }

  return population[population.length - 1];
}

/**
 * エリート選択
 *
 * 適応度上位n個体を選択。
 *
 * @param population - 個体群
 * @param count - 選択数
 * @returns 選択された個体群
 */
export function eliteSelection(
  population: Individual[],
  count: number
): Individual[] {
  const sorted = [...population].sort((a, b) => b.fitness - a.fitness);
  return sorted.slice(0, count);
}

// =============================================================================
// 複合オペレータ
// =============================================================================

/**
 * デフォルトの交叉オペレータ
 */
export function crossover(parent1: AIGenome, parent2: AIGenome): AIGenome {
  return blendCrossover(parent1, parent2, 0.3);
}

/**
 * デフォルトの突然変異オペレータ
 */
export function mutate(
  genome: AIGenome,
  rate: number = 0.1,
  strength: number = 0.2
): AIGenome {
  return gaussianMutation(genome, rate, strength);
}
